import {
  Button,
  ButtonGroup,
  FormLabel,
  ModalBody,
  ModalFooter,
  Radio,
  RadioGroup,
  SelectionControlLabel,
} from '@one-thd/sui-atomic-components';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { AxiosError, AxiosResponse } from 'axios';
import React, { useState, ChangeEvent } from 'react';
import { quickbooksLogin } from '../../../../api/oms/order-history';
import { IAlert } from '../../../../models/common';
import { IPHRequest } from '../../../../models/purchase-history';
import {
  TQBRadioButton,
  TQBRenderFlag,
} from '../../../../models/purchase-history/quick-books-export';
import {
  pushErrorEvent,
  pushEventInfo,
  setPageInfoWithEvent,
} from '../../../../utils/analytics';
import { getWindow } from '../../../../utils/general';
import { pushQuickBookExportEvent } from '../../../../utils/purchase-history/analytics';

export interface IQuickBooksOnlineExport {
  setQBRenderFlag: (value: TQBRenderFlag) => void;
  request: IPHRequest;
  setOpenModal: (value: boolean) => void;
  setAlertState: (value: IAlert) => void;
}

const handleSuccessOnlineRequest = (
  response: AxiosResponse,
  setOpenModal: (value: boolean) => void,
): void => {
  const qbLoginUrl = getWindow()?.atob(response.data.qbLoginUrl);

  setOpenModal(false);

  const qbWindow = getWindow()?.open(
    qbLoginUrl,
    'QuickBooks Login',
    'top=0,left=0,height=650px,width=auto,scrollbars=yes,resizable=yes,noopener,noreferrer',
  );

  if (qbWindow) {
    qbWindow.opener = null;
  }
};

const handleFailureOnlineRequest = (
  error: AxiosError,
  setAlertState: (value: IAlert) => void,
  setOpenModal: (value: boolean) => void,
) => {
  pushErrorEvent(
    'purchase history quickbooks online export',
    error.code || '',
    error.message,
    'inline',
  );
  setAlertState({
    status: 'error',
    alertMessage: 'We ran into a problem, please try again.',
  });
  setOpenModal(false);
};
const QuickBooksLoginPassAnalytics = () => {
  pushEventInfo({
    category: { primaryCategory: 'my account' },
    eventInfo: { eventName: 'my account>purchase history' },
    myAccount: { settings: 'my account>purchase history>quickbooks sync pass' },
  });
  setPageInfoWithEvent(
    'account',
    'account>purchase history',
    'account>purchase history>quickbooks sync complete',
    'account>purchase history>quickbooks sync complete',
    'maml:orders',
    'quickbooks sync pass',
  );
};

const QuickBooksLoginFailAnalytics = () => {
  pushEventInfo({
    category: { primaryCategory: 'my account' },
    eventInfo: { eventName: 'my account>purchase history' },
    myAccount: { settings: 'my account>purchase history>quickbooks sync fail' },
  });
  setPageInfoWithEvent(
    'account',
    'account>purchase history',
    'account>purchase history>quickbooks sync fail',
    'account>purchase history>quickbooks sync fail',
    'maml:orders',
    'quickbooks sync fail',
  );
};

const handleOnlineExportClick = (
  request: IPHRequest,
  qbExportValue: TQBRadioButton,
  setOpenModal: (value: boolean) => void,
  setAlertState: (value: IAlert) => void,
  userId: string,
) => {
  const updatedRequest = request;
  if (qbExportValue === 'details') {
    updatedRequest.includeDetails = true;
  }
  const wrappedRequest = { orderHistoryRequest: updatedRequest };
  pushQuickBookExportEvent(
    `purchase history: quickbooks export online ${qbExportValue}`,
  );
  quickbooksLogin(wrappedRequest, userId)
    .then((response: AxiosResponse) => {
      handleSuccessOnlineRequest(response, setOpenModal);
      QuickBooksLoginPassAnalytics();
    })
    .catch((error: AxiosError) => {
      QuickBooksLoginFailAnalytics();
      handleFailureOnlineRequest(error, setAlertState, setOpenModal);
    });
};

const QuickBooksOnlineExport = (props: IQuickBooksOnlineExport) => {
  const { userID } = useThdCustomer() || {};
  const { setQBRenderFlag, request, setOpenModal, setAlertState } = props;

  const [radioValue, setRadioValue] = useState<TQBRadioButton>();

  return (
    <>
      <ModalBody>
        <FormLabel id="online-qb-export-radio-buttons-group-label">
          Select data to export:
        </FormLabel>
        <RadioGroup
          aria-labelledby="online-qb-export-radio-buttons-group-label"
          name="online-qb-export-radio-buttons-group"
          value={radioValue}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setRadioValue(event.target.value as TQBRadioButton);
          }}
        >
          <SelectionControlLabel value="summary" label="Summary Data">
            <Radio />
          </SelectionControlLabel>
          <SelectionControlLabel value="details" label="Details Data">
            <Radio />
          </SelectionControlLabel>
        </RadioGroup>
      </ModalBody>
      <ModalFooter position="end">
        <ButtonGroup>
          <Button
            onClick={() => {
              setQBRenderFlag(undefined);
            }}
          >
            Go Back
          </Button>
          <Button
            disabled={radioValue === undefined}
            onClick={() =>
              handleOnlineExportClick(
                request,
                radioValue,
                setOpenModal,
                setAlertState,
                userID,
              )
            }
          >
            Submit
          </Button>
        </ButtonGroup>
      </ModalFooter>
    </>
  );
};

export default QuickBooksOnlineExport;
